/**
 * useWidgets - Hook for managing widgets
 *
 * Fetches all widgets directly from the widgets endpoint.
 */

import { useMemo } from "react";
import { create } from "zustand";
import { useWidgets as useWidgetsApi } from "@/api/widget";
import { BackendWidget, getBackendWidgetType } from "@/models/widget-v2";

// ============================================================================
// Types
// ============================================================================

export interface WidgetWithContext {
  /** Unique identifier (widget id or generated) */
  id: string;
  /** The widget configuration */
  widget: BackendWidget;
  /** Dashboard ID if widget belongs to a dashboard */
  dashboardId?: string;
  /** Dashboard name for display purposes */
  dashboardName?: string;
}

/**
 * Get widget type label in French
 * Aligné sur les doc_type backend (underscore(ClassName))
 */
export function getWidgetTypeLabel(chartType: string | undefined): string {
  const labels: Record<string, string> = {
    "line_widget": "Graphique en ligne",
    "bar_widget": "Graphique en barres",
    "area_widget": "Graphique en aire",
    "pie_widget": "Graphique circulaire",
    "scatter_chart_widget": "Nuage de points",
    "gauge_widget": "Jauge",
    "heatmap_widget": "Carte de chaleur",
    "treemap_widget": "Treemap",
    "word_cloud_widget": "Nuage de mots",
    "table_widget": "Tableau",
    "kpi_widget": "Carte KPI",
    "kpi_comparison_widget": "Carte KPI (comparaison)",
    "documents_widget": "Documents",
    "graph_widget": "Graphe relationnel",
    "map_widget": "Carte",
    "text_widget": "Texte",
  };
  return labels[chartType || ""] || chartType || "Widget";
}

/**
 * Get widget icon emoji
 * Aligné sur les doc_type backend (underscore(ClassName))
 */
export function getWidgetIcon(chartType: string | undefined): string {
  const icons: Record<string, string> = {
    "line_widget": "📈",
    "bar_widget": "📊",
    "area_widget": "📉",
    "pie_widget": "🥧",
    "scatter_chart_widget": "⚬",
    "gauge_widget": "🎯",
    "heatmap_widget": "🔥",
    "treemap_widget": "🌳",
    "word_cloud_widget": "☁️",
    "table_widget": "📋",
    "kpi_widget": "🎯",
    "kpi_comparison_widget": "🎯",
    "documents_widget": "📄",
    "graph_widget": "🕸️",
    "map_widget": "🗺️",
    "text_widget": "📝",
  };
  return icons[chartType || ""] || "📊";
}

interface WidgetsStore {
  selectedWidgetId: string | null;
  setSelectedWidgetId: (id: string | null) => void;
}

// ============================================================================
// Store
// ============================================================================

export const useWidgetsStore = create<WidgetsStore>((set) => ({
  selectedWidgetId: null,
  setSelectedWidgetId: (id) => set({ selectedWidgetId: id }),
}));

// ============================================================================
// Hook
// ============================================================================

export function useWidgets() {
  const { data: widgetsData, isLoading, error } = useWidgetsApi();
  const { selectedWidgetId, setSelectedWidgetId } = useWidgetsStore();

  // Transform widgets to WidgetWithContext format
  const allWidgets = useMemo(() => {
    if (!widgetsData) return [];
    
    return widgetsData.map((widget): WidgetWithContext => ({
      id: widget.id || `widget-${Math.random().toString(36).substr(2, 9)}`,
      widget,
      dashboardId: widget.dashboard?.id,
      // Note: dashboardName would need to be fetched separately if needed
      // For now, we use the dashboard ID as a fallback
      dashboardName: widget.dashboard?.id ? `Dashboard ${widget.dashboard.id.slice(0, 8)}...` : undefined,
    }));
  }, [widgetsData]);

  // Group widgets by type
  const widgetsByType = useMemo(() => {
    const grouped = new Map<string, WidgetWithContext[]>();
    for (const widget of allWidgets) {
      const type = getBackendWidgetType(widget.widget, "unknown" as any);
      if (!grouped.has(type)) {
        grouped.set(type, []);
      }
      grouped.get(type)!.push(widget);
    }
    return grouped;
  }, [allWidgets]);

  // Get selected widget
  const selectedWidget = useMemo(() => {
    if (!selectedWidgetId) return null;
    return allWidgets.find(w => w.id === selectedWidgetId) || null;
  }, [allWidgets, selectedWidgetId]);

  // Select widget action
  const selectWidget = (id: string | null) => {
    setSelectedWidgetId(id);
  };

  return {
    // Data
    widgets: allWidgets,
    widgetsByType,
    selectedWidget,
    selectedWidgetId,
    
    // Loading states
    isLoading,
    error,
    
    // Actions
    selectWidget,
  };
}

export default useWidgets;
